import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGridPro, GridRenderCellParams, type GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { getCustomerAccessCheckResults } from "../../api-v2/customer-access";
import type { ServerCustomerAccess } from "../../api-v2/types/server-customer-access";

interface AccessCheckModalProps {
  open: boolean;
  onClose: () => void;
}

const TooltipListCell = (props: GridRenderCellParams<any, string[]>) => {
  const errors = (props.value?.sort() as string[]) ?? [];
  return (
    <Tooltip title={errors.join(", ")} placement="top" arrow>
      <Box display="flex" width="100%">
        {errors.join(", ")}
      </Box>
    </Tooltip>
  );
};

const columns: GridColDef<ServerCustomerAccess>[] = [
  { field: "vendorId", headerName: "Vendor Id", flex: 1, valueGetter: (value: string) => "V" + value },
  { field: "vendorName", headerName: "Vendor Name", flex: 1 },
  { field: "accountId", headerName: "Account Id", flex: 1, valueGetter: (value: string) => "S" + value },
  { field: "name", headerName: "Name", flex: 1 },
  {
    field: "extractionErrors",
    headerName: "Extraction Errors",
    flex: 1,
    sortable: false,
    renderCell: props => <TooltipListCell {...props} />,
  },
  {
    field: "reportTypes",
    headerName: "Report Types",
    flex: 1,
    sortable: false,
    renderCell: props => <TooltipListCell {...props} />,
  },
  { field: "daysSinceLastSuccess", headerName: "Days Since Last Success", flex: 1 },
  { field: "csm", headerName: "CSM", flex: 1 },
];

const AccessCheckModal = ({ open, onClose }: AccessCheckModalProps) => {
  const dataQuery = useQuery({
    queryKey: ["customers", "access"],
    queryFn: ({ signal }) => getCustomerAccessCheckResults({ signal }),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Customer Access Check
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {dataQuery.isError ? (
          <span>Failed to load list of customers with access issues.</span>
        ) : (
          <Box sx={{ height: 600 }}>
            <DataGridPro<ServerCustomerAccess>
              rows={dataQuery.data?.customers ?? []}
              columns={columns}
              getRowId={row => `${row.vendorId}-${row.accountId}`}
              loading={dataQuery.isFetching}
              headerFilters
              rowCount={dataQuery.data?.total_records ?? 0}
              rowSelection={false}
              sx={{
                "& .MuiFormControl-root": {
                  label: { display: "none" },
                  "& .MuiInputBase-root": { marginTop: "0" },
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccessCheckModal;
